import React, { useState } from "react";
import {
    Box,
    Button,
    Autocomplete,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    IconButton,
    Popover,
    FormControlLabel,
    Switch,
    Checkbox
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
    format,
    addWeeks,
    subWeeks,
    addDays,
    subDays,
    addMonths,
    subMonths,
    startOfWeek,
    endOfWeek
} from "date-fns";

/**
 * CalendarToolbar - Top toolbar with provider selector, date navigation,
 * view switcher, and add booking button.
 */
export default function CalendarToolbar({
    providers,
    selectedProviders,
    onProviderChange,
    currentDate,
    onDateChange,
    view,
    onViewChange,
    cellDuration,
    onCellDurationChange,
    showSaturday,
    onShowSaturdayChange,
    showSunday,
    onShowSundayChange,
    onAddBooking,
    isLoading,
    hideProviderSelector,
    canAddBooking = true
}) {
    const [settingsAnchor, setSettingsAnchor] = useState(null);

    const handlePrev = () => {
        if (view === "Day") {
            onDateChange(subDays(currentDate, 1));
        } else if (view === "Month") {
            onDateChange(subMonths(currentDate, 1));
        } else {
            onDateChange(subWeeks(currentDate, 1));
        }
    };

    const handleNext = () => {
        if (view === "Day") {
            onDateChange(addDays(currentDate, 1));
        } else if (view === "Month") {
            onDateChange(addMonths(currentDate, 1));
        } else {
            onDateChange(addWeeks(currentDate, 1));
        }
    };

    const handleToday = () => {
        onDateChange(new Date());
    };

    const getDateLabel = () => {
        if (view === "Day") {
            return format(currentDate, "EEEE, MMMM d, yyyy");
        }
        if (view === "Month") {
            return format(currentDate, "MMMM yyyy");
        }
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        if (weekStart.getMonth() === weekEnd.getMonth()) {
            return `${format(weekStart, "MMM d")} - ${format(weekEnd, "d, yyyy")}`;
        }
        return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #e0e0e0",
                flexWrap: "wrap",
                backgroundColor: "#fff"
            }}
        >
            {/* Provider selector (hidden for provider-role users) */}
            {!hideProviderSelector && <Autocomplete
                multiple
                size="small"
                options={providers || []}
                getOptionLabel={(option) =>
                    option.name ||
                    `${option.firstname || ""} ${option.lastname || ""}`.trim()
                }
                value={selectedProviders || []}
                onChange={(_, newValue) => onProviderChange(newValue)}
                isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                }
                renderOption={(props, option, { selected }) => (
                    <li {...props}>
                        <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            checked={selected}
                            sx={{ mr: 1, p: 0 }}
                        />
                        <Box>
                            <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                                {option.name ||
                                    `${option.firstname || ""} ${option.lastname || ""}`.trim()}
                            </Typography>
                            {option.emailaddress && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.7rem", lineHeight: 1 }}
                                >
                                    {option.emailaddress}
                                </Typography>
                            )}
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={
                            selectedProviders?.length
                                ? ""
                                : "Select providers..."
                        }
                        variant="outlined"
                    />
                )}
                sx={{ minWidth: 250, maxWidth: 400, flex: 1 }}
                loading={isLoading}
                disableCloseOnSelect
            />}

            {/* Date navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="small" onClick={handlePrev}>
                    <ChevronLeftIcon />
                </IconButton>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleToday}
                    startIcon={<TodayIcon />}
                    sx={{ textTransform: "none", minWidth: "auto" }}
                >
                    Today
                </Button>
                <IconButton size="small" onClick={handleNext}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            {/* Date label */}
            <Typography
                variant="subtitle1"
                fontWeight={500}
                sx={{ minWidth: 160, textAlign: "center" }}
            >
                {getDateLabel()}
            </Typography>

            {/* View switcher */}
            <ToggleButtonGroup
                size="small"
                exclusive
                value={view}
                onChange={(_, newView) => {
                    if (newView) onViewChange(newView);
                }}
            >
                <ToggleButton value="Week" sx={{ textTransform: "none" }}>
                    Week
                </ToggleButton>
                <ToggleButton value="Day" sx={{ textTransform: "none" }}>
                    Day
                </ToggleButton>
                <ToggleButton value="Month" sx={{ textTransform: "none" }}>
                    Month
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Cell duration toggle */}
            <ToggleButtonGroup
                size="small"
                exclusive
                value={cellDuration}
                onChange={(_, newVal) => {
                    if (newVal) onCellDurationChange(newVal);
                }}
            >
                <ToggleButton value={15} sx={{ textTransform: "none" }}>
                    15m
                </ToggleButton>
                <ToggleButton value={30} sx={{ textTransform: "none" }}>
                    30m
                </ToggleButton>
                <ToggleButton value={60} sx={{ textTransform: "none" }}>
                    60m
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Settings gear */}
            <IconButton
                size="small"
                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                sx={{
                    color: settingsAnchor ? "primary.main" : "text.secondary",
                    "&:hover": { color: "primary.main" }
                }}
            >
                <SettingsOutlinedIcon fontSize="small" />
            </IconButton>

            <Popover
                open={Boolean(settingsAnchor)}
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 2,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            mt: 0.5
                        }
                    }
                }}
            >
                <Box sx={{ px: 2, py: 1.5, minWidth: 180 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: "0.65rem",
                            mb: 0.5,
                            display: "block"
                        }}
                    >
                        Show days
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showSaturday}
                                onChange={(e) =>
                                    onShowSaturdayChange(e.target.checked)
                                }
                            />
                        }
                        label={
                            <Typography variant="body2">Saturday</Typography>
                        }
                        sx={{ ml: 0, width: "100%" }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showSunday}
                                onChange={(e) =>
                                    onShowSundayChange(e.target.checked)
                                }
                            />
                        }
                        label={
                            <Typography variant="body2">Sunday</Typography>
                        }
                        sx={{ ml: 0, width: "100%" }}
                    />
                </Box>
            </Popover>

            {/* Add booking button */}
            {canAddBooking && (
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAddBooking}
                    sx={{ textTransform: "none", ml: "auto" }}
                >
                    Add Booking
                </Button>
            )}
        </Box>
    );
}
